import { useEffect, useMemo, useRef, useState } from 'react'
import { useBuildingStore } from '../../stores/buildingStore'
import { useAuthStore } from '../../stores/authStore'
import { preferencesApi } from '../../services/preferencesApi'
import type { Site } from '../../types'

export default function SiteSelector() {
  const sites = useBuildingStore((s) => s.sites)
  const currentSiteId = useBuildingStore((s) => s.currentSiteId)
  const loadSite = useBuildingStore((s) => s.loadSite)
  const fetchSites = useBuildingStore((s) => s.fetchSites)
  const token = useAuthStore((s) => s.token)

  const [defaultSiteId, setDefaultSiteId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const loadedRef = useRef(false)

  // Fetch all sites on mount if not already loaded
  useEffect(() => {
    if (token && sites.length === 0) {
      fetchSites()
    }
  }, [token, sites.length, fetchSites])

  // Load user preferences and auto-load default site
  useEffect(() => {
    if (!token || loadedRef.current) return
    loadedRef.current = true
    preferencesApi.get().then((prefs) => {
      setDefaultSiteId(prefs.default_site_id)
      if (prefs.default_site_id && !currentSiteId) {
        loadSite(prefs.default_site_id)
      }
    }).catch(() => {})
  }, [token]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSetDefault = async () => {
    if (!currentSiteId) return
    setSaving(true)
    try {
      const prefs = await preferencesApi.update({ default_site_id: currentSiteId })
      setDefaultSiteId(prefs.default_site_id)
    } catch {
      // ignore
    } finally {
      setSaving(false)
    }
  }

  // Group sites by client_name for display
  const grouped = useMemo(() => {
    const groups = new Map<string, Site[]>()
    for (const site of sites) {
      const key = site.client_name ?? 'Autres'
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key)!.push(site)
    }
    return groups
  }, [sites])

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const siteId = e.target.value
    if (siteId) {
      loadSite(siteId)
    }
  }

  const hasMultipleClients = grouped.size > 1
  const isDefault = currentSiteId != null && currentSiteId === defaultSiteId

  return (
    <div className="site-selector-wrapper">
      <select
        className="site-selector"
        value={currentSiteId ?? ''}
        onChange={handleChange}
      >
        <option value="">-- Choisir un site --</option>
        {hasMultipleClients
          ? Array.from(grouped.entries()).map(([clientName, clientSites]) => (
              <optgroup key={clientName} label={clientName}>
                {clientSites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.id === defaultSiteId ? '★ ' : ''}{site.name}
                  </option>
                ))}
              </optgroup>
            ))
          : sites.map((site) => (
              <option key={site.id} value={site.id}>
                {site.id === defaultSiteId ? '★ ' : ''}{site.name}
              </option>
            ))}
      </select>
      {currentSiteId && !isDefault && (
        <button
          className="site-selector__default-btn"
          onClick={handleSetDefault}
          disabled={saving}
          title="Definir comme site par defaut"
        >
          {saving ? '...' : '★'}
        </button>
      )}
      {isDefault && (
        <span className="site-selector__default-badge" title="Site par defaut">★</span>
      )}
    </div>
  )
}
